import React, { useState, useEffect } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";

const Home = () => {
  const navigate = useNavigate();

  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [availabilityData, setAvailabilityData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const SIGNIN_API_URL = process.env.REACT_APP_SIGNIN_API_URL;

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await axios.get(`${SIGNIN_API_URL}/team-members`);
        setTeamMembers(response.data);
      } catch (err) {
        console.error("Error fetching team members:", err);
        setError("Failed to fetch team members.");
      }
    };

    fetchTeamMembers();
  }, []);

  const fetchAvailability = async () => {
    if (!selectedUserId || !startDate || !endDate) {
      setError("Please select a user and date range.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${SIGNIN_API_URL}/team-availability`, {
        params: {
          start_time: `${startDate}T00:00:00Z`,
          end_time: `${endDate}T23:59:59Z`,
          userId: selectedUserId,
        },
      });
      setAvailabilityData(response.data);
    } catch (err) {
      console.error("Error fetching availability:", err);
      setError("Failed to fetch availability data.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    const confirmLogout = window.confirm("Are you sure you want to log out?");
    if (confirmLogout) {
      AsyncStorage.removeItem("token");
      navigate("/", { replace: true });
    }
  };

  const convertToUKTime = (utcDate) => {
    return DateTime.fromISO(utcDate, { zone: "utc" })
      .setZone("Europe/London")
      .toFormat("HH:mm");
  };

  return (
    <div
      style={{
        fontFamily: "Arial, sans-serif",
        backgroundColor: "#f4f4f9",
      }}
    >
      <div
        style={{
          margin: "0 auto",
          backgroundColor: "#fff",
          borderRadius: "8px",
          padding: "20px",
        }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h1 style={{ color: "#333" }}>Clinician Availability View</h1>
          <button
            onClick={handleLogout}
            style={{
              padding: "10px 20px",
              backgroundColor: "#e63946",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            Logout
          </button>
        </header>

        <div
          style={{
            marginBottom: "100px",
            display: "flex",
            gap: "50px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div>
            <label style={{ fontWeight: "bold" }}>Select User:</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              style={{
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                width: "100%",
                boxSizing: "border-box",
              }}
              onFocus={(e) => (e.target.style.outline = "1px solid black")}
              onBlur={(e) => (e.target.style.outline = "1px #ddd")}
            >
              <option value="">-- Select a User --</option>
              {teamMembers.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ minWidth: "200px" }}>
            <label
              style={{
                fontWeight: "bold",
                display: "block",
                marginBottom: "5px",
              }}
            >
              Start Date:
            </label>
            <input
              type="date"
              onClick={(e) => e.target.showPicker()}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                width: "100%",
              }}
              onFocus={(e) => (e.target.style.outline = "1px solid black")}
              onBlur={(e) => (e.target.style.outline = "1px #ddd")}
            />
          </div>

          <div style={{ minWidth: "200px" }}>
            <label
              style={{
                fontWeight: "bold",
                display: "block",
                marginBottom: "5px",
              }}
            >
              End Date:
            </label>
            <input
              type="date"
              value={endDate}
              onClick={(e) => e.target.showPicker()}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                width: "100%",
              }}
              onFocus={(e) => (e.target.style.outline = "1px solid black")}
              onBlur={(e) => (e.target.style.outline = "1px #ddd")}
            />
          </div>

          <button
            onClick={fetchAvailability}
            style={{
              padding: "10px 20px",
              backgroundColor: "#457b9d",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "16px",
              alignSelf: "end",
            }}
          >
            Fetch Availability
          </button>
        </div>
        {error && (
          <p style={{ color: "#e63946", fontWeight: "bold" }}>{error}</p>
        )}
        <div style={{ height: "10px" }}>
          {loading && (
            <p style={{ marginTop: "20px", color: "#333" }}>Loading...</p>
          )}
        </div>

        {availabilityData.length > 0 ? (
          <table
            style={{
              marginTop: "20px",
              width: "100%",
              borderCollapse: "collapse",
              backgroundColor: "#fafafa",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#457b9d", color: "white" }}>
                <th
                  style={{
                    padding: "10px",
                    border: "1px solid #ddd",
                    textAlign: "center",
                  }}
                >
                  Date
                </th>
                <th
                  style={{
                    padding: "10px",
                    border: "1px solid #ddd",
                    textAlign: "center",
                  }}
                >
                  Day
                </th>
                <th
                  style={{
                    padding: "10px",
                    border: "1px solid #ddd",
                    textAlign: "center",
                  }}
                >
                  Availability Ranges
                </th>
                <th
                  style={{
                    padding: "10px",
                    border: "1px solid #ddd",
                    textAlign: "center",
                  }}
                >
                  Unavailable Times
                </th>
                <th
                  style={{
                    padding: "10px",
                    border: "1px solid #ddd",
                    textAlign: "center",
                  }}
                >
                  Scheduled Hours
                </th>
                <th
                  style={{
                    padding: "10px",
                    border: "1px solid #ddd",
                    textAlign: "center",
                  }}
                >
                  Available Hours
                </th>
              </tr>
            </thead>
            <tbody>
              {availabilityData.map((item, index) => {
                const isAvailabilityHighlighted =
                  item.availabilityRanges.length > 0;

                return (
                  <tr
                    key={index}
                    style={{
                      backgroundColor: isAvailabilityHighlighted
                        ? "#e6f9e6"
                        : "transparent",
                    }}
                  >
                    <td
                      style={{
                        padding: "10px",
                        border: "1px solid #ddd",
                        textAlign: "center",
                      }}
                    >
                      {item.date}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        border: "1px solid #ddd",
                        textAlign: "center",
                      }}
                    >
                      {item.day}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        border: "1px solid #ddd",
                        textAlign: "center",
                      }}
                    >
                      {isAvailabilityHighlighted
                        ? item.availabilityRanges
                            .map((range) => `${range.from} - ${range.to}`)
                            .join(", ")
                        : "None"}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        border: "1px solid #ddd",
                        textAlign: "center",
                      }}
                    >
                      {item.unavailableTimes.length > 0
                        ? item.unavailableTimes
                            .map(
                              (time) =>
                                `${convertToUKTime(
                                  time.start_time
                                )} - ${convertToUKTime(time.end_time)}`
                            )
                            .join(", ")
                        : "None"}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        border: "1px solid #ddd",
                        textAlign: "center",
                      }}
                    >
                      {item.totalScheduledHours}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        border: "1px solid #ddd",
                        textAlign: "center",
                      }}
                    >
                      {item.totalAvailableHours}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div
            style={{
              marginTop: "20px",
              padding: "20px",
              backgroundColor: "#f8d7da",
              color: "#721c24",
              border: "1px solid #f5c6cb",
              borderRadius: "4px",
              textAlign: "center",
            }}
          >
            No data is available.
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
